import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import Nav from "../landing-pages/home-two/components/nav";
import { MTN_YELLOW } from "../../constants/Colors";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import {
  BAND_COLOURS,
  BAND_INTERVAL_MS,
  MAX_BANDS,
  weaveDurationMs,
  type BandColour,
} from "../../components/games/kente/weave-config";
import type { WovenBand } from "../../components/games/kente/KenteWeave";

/**
 * Throwaway spike for the Kae Kente weave animation (Ken, 2026-09-04:
 * see the weave before committing to the rest of the game). It is the one
 * part that decides whether this feels expensive or cheap, so it gets looked
 * at on a real phone before anything else is built.
 *
 * Not linked from the games hub, and not the game: there is no round, no
 * scoring and no recall step here. The colour buttons only re-weave.
 *
 * three.js is lazy-loaded, so the homepage and the campaign chat never carry
 * it.
 */
const KenteWeave = lazy(() => import("../../components/games/kente/KenteWeave"));

function randomSequence(length: number): BandColour[] {
  const out: BandColour[] = [];
  for (let i = 0; i < length; i += 1) {
    const pick = BAND_COLOURS[Math.floor(Math.random() * BAND_COLOURS.length)];
    // No immediate repeats: two identical bands in a row read as one thick
    // band, which would make the sequence ambiguous to the player.
    if (i > 0 && pick.key === out[i - 1].key) {
      const alternatives = BAND_COLOURS.filter((c) => c.key !== pick.key);
      out.push(alternatives[Math.floor(Math.random() * alternatives.length)]);
      continue;
    }
    out.push(pick);
  }
  return out;
}

export default function WeaveSpike() {
  useHideEbanqoWidget("kente-weave-spike");

  const [length, setLength] = useState(5);
  const [bands, setBands] = useState<WovenBand[]>([]);
  const [showTicks, setShowTicks] = useState(true);
  const [weaving, setWeaving] = useState(false);
  const settleTimer = useRef<number | null>(null);

  const weave = useCallback((nextLength: number) => {
    if (settleTimer.current !== null) {
      window.clearTimeout(settleTimer.current);
    }
    const startAt = performance.now() + 120;
    setLength(nextLength);
    setBands(
      randomSequence(nextLength).map((colour, index) => ({
        colour,
        appearAtMs: startAt + index * BAND_INTERVAL_MS,
      })),
    );
    setWeaving(true);
    settleTimer.current = window.setTimeout(
      () => setWeaving(false),
      120 + weaveDurationMs(nextLength),
    );
  }, []);

  // Weave once on arrival, and tidy the timer on the way out.
  useEffect(() => {
    weave(5);
    return () => {
      if (settleTimer.current !== null) {
        window.clearTimeout(settleTimer.current);
      }
    };
  }, [weave]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-950 text-white">
      {/* text-black: this page is dark, and Nav is a light bar whose
          own labels inherit colour — without this the Log In button
          renders white on white. */}
      <div className="text-black">
        <Nav />
      </div>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-6 sm:px-6">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="m-0 text-xl font-bold">Kente weave — spike</h1>
          <Link to="/play" className="text-xs text-white/50 underline">
            Games
          </Link>
        </div>
        <p className="mt-1 text-xs text-white/50">
          Animation only — for tuning the rhythm and the strip. The game
          itself is at{" "}
          <Link to="/play/kae-kente" className="underline">
            /play/kae-kente
          </Link>
          .
        </p>

        {/* The canvas gets a fixed aspect box so the strip cannot jump around
            as controls change height. */}
        <div className="mt-5 h-[52vh] min-h-[320px] w-full overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-white/40">
                Loading the loom…
              </div>
            }
          >
            <KenteWeave
              slotCount={length}
              bands={bands}
              showTicks={showTicks}
            />
          </Suspense>
        </div>

        <div className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => weave(length)}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-black transition-colors hover:brightness-95"
              style={{ backgroundColor: MTN_YELLOW }}
            >
              Weave again
            </button>
            <span className="text-xs text-white/40">
              {weaving
                ? "weaving…"
                : `${length} bands · ${(weaveDurationMs(length) / 1000).toFixed(1)}s`}
            </span>
          </div>

          <div>
            <label
              htmlFor="band-count"
              className="block text-xs font-medium text-white/60"
            >
              Sequence length: {length}
            </label>
            <input
              id="band-count"
              type="range"
              min={3}
              max={MAX_BANDS}
              value={length}
              onChange={(event) => weave(Number(event.target.value))}
              className="mt-2 w-full max-w-xs accent-[#FFCC08]"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-white/60">
            <input
              type="checkbox"
              checked={showTicks}
              onChange={(event) => setShowTicks(event.target.checked)}
              className="accent-[#FFCC08]"
            />
            Motif ticks (the non-colour channel — 1 for gold, 2 green, 3 red, 4
            blue)
          </label>

          <div className="flex flex-wrap gap-2 pt-1">
            {BAND_COLOURS.map((colour) => (
              <span
                key={colour.key}
                className="inline-flex items-center gap-2 rounded-md bg-white/5 px-2.5 py-1 text-[11px] text-white/70"
              >
                <span
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: colour.hex }}
                  aria-hidden
                />
                {colour.label} · {colour.ticks}
              </span>
            ))}
          </div>

          <p className="pt-2 text-[11px] leading-relaxed text-white/35">
            Colour bands in the kente idiom, not reproductions of named cloths
            — named patterns carry meaning and some are tied to royalty, so
            naming is left to people who hold that tradition.
          </p>
        </div>
      </main>
    </div>
  );
}
